/** Phase 5 command shapes: insurance, vendors, message templates, drip
 * sequences and the review queue (SPEC §3.5, §3.8, §7.9–7.12).
 *
 * Primitives come from `common.ts` so a date, an id or a phone number means the
 * same thing here as everywhere else.
 */
import { z } from "zod";
import { id, localDateTime } from "./common";

// ---------------------------------------------------------------- insurance

/** Phase 3 already owns the staff-side insurance editor (`@shared/schemas/review`
 * `insurancePatch`, guarded by the review source hash). Phase 5 adds only the
 * client's own upload, so all that belongs here is the file itself. */
export const INSURANCE_MAX_BYTES = 10 * 1024 * 1024;

/** A client's upload names nothing but the file; the server sniffs the bytes and
 * picks the stored name itself. */
export const insuranceFileMetadata = z.object({ name: z.string().min(1).max(255), size: z.number().int().min(1).max(INSURANCE_MAX_BYTES) });

// ---------------------------------------------------------------- vendors

export const coiStatus = z.enum(["pending", "received", "waived", "overdue"]);
export const vendorCategory = z.string().trim().max(80);

export const bookingVendorSave = z.object({
  name: z.string().trim().min(1).max(160),
  category: vendorCategory.nullable().optional(),
  contactName: z.string().trim().max(160).nullable().optional(),
  phone: z.string().trim().max(40).nullable().optional(),
  email: z.string().trim().max(200).nullable().optional(),
  role: z.string().trim().max(160).nullable().optional(),
  arrivalAt: localDateTime.nullable().optional(),
  coiStatus: coiStatus.optional(),
  notes: z.string().trim().max(2000).nullable().optional(),
});
export type BookingVendorSave = z.infer<typeof bookingVendorSave>;

export const bookingVendorPatch = bookingVendorSave.partial().strict()
  .refine(value => Object.keys(value).length > 0, "Choose a vendor detail to change.");
export type BookingVendorPatch = z.infer<typeof bookingVendorPatch>;

export const bookingVendorRecord = z.object({
  name: z.string().min(1).max(160),
  coiStatus,
  email: z.string().max(200).nullable(),
  phone: z.string().max(40).nullable(),
});

// ---------------------------------------------------------------- templates

/** Only the placeholders `render.ts` can resolve. Anything else would reach a
 * client as `{{…}}`, so it is rejected when the template is saved rather than
 * discovered on the tick. */
export const TEMPLATE_PLACEHOLDERS = [
  "first_name", "last_name", "company", "venue", "event_date",
  "amount", "due_date", "link", "agent_name", "agent_phone", "unsubscribe_url",
] as const;

export const messageTemplateSave = z.object({
  key: z.string().trim().min(1).max(80).regex(/^[a-z0-9][a-z0-9_.+-]*$/, "A template key is lower case letters, numbers, dots, dashes and underscores."),
  name: z.string().trim().min(1).max(160),
  smsBody: z.string().trim().max(1600).nullable().optional(),
  emailSubject: z.string().trim().max(200).nullable().optional(),
  emailBody: z.string().trim().max(20000).nullable().optional(),
  notes: z.string().trim().max(2000).nullable().optional(),
  active: z.boolean().optional(),
});
export type MessageTemplateSave = z.infer<typeof messageTemplateSave>;

export const messageTemplatePatch = messageTemplateSave.omit({ key: true }).partial().strict()
  .refine(value => Object.keys(value).length > 0, "Change something before saving the template.");
export type MessageTemplatePatch = z.infer<typeof messageTemplatePatch>;

export const messageTemplatePreview = z.object({ channel: z.enum(["sms", "email"]).default("email") });

export const messageTemplateRecord = z.object({
  key: z.string().min(1).max(80),
  name: z.string().max(160).nullable(),
  smsBody: z.string().nullable(),
  emailSubject: z.string().nullable(),
  emailBody: z.string().nullable(),
  active: z.number().int().min(0).max(1),
});

// ---------------------------------------------------------------- sequences

export const dripSequenceToggle = z.object({ active: z.boolean() });
export const dripEnrollmentAction = z.object({ action: z.enum(["pause", "cancel", "resume"]), days: z.number().int().min(1).max(90).optional() });

// ---------------------------------------------------------------- queue

export const queueQuery = z.object({
  kind: z.string().trim().max(40).optional(),
  channel: z.enum(["sms", "email"]).optional(),
  q: z.string().trim().max(120).default(""),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type QueueQuery = z.infer<typeof queueQuery>;

export const queueEdit = z.object({
  body: z.string().trim().min(1).max(20000).optional(),
  subject: z.string().trim().min(1).max(200).optional(),
}).refine(value => Object.keys(value).length > 0, "Change the message before saving it.");

export const queueIds = z.object({ ids: z.array(id).min(1).max(200) });
export type QueueIds = z.infer<typeof queueIds>;

// ---------------------------------------------------------------- settings

export const quietHoursSetting = z.object({
  start: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Use a 24-hour time such as 21:00."),
  end: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Use a 24-hour time such as 08:00."),
});

export const automationSettingsPatch = z.object({
  autoSendAutomated: z.boolean().optional(),
  quietHours: quietHoursSetting.optional(),
}).strict().refine(value => Object.keys(value).length > 0, "Choose an automation setting to change.");
export type AutomationSettingsPatch = z.infer<typeof automationSettingsPatch>;

export const calendarConnect = z.object({ googleCalendarId: z.string().trim().min(1).max(200).nullable() });
